/**
 * API Client für Products
 * 
 * Verwendet workspace_integration_id als Referenz.
 */
import { apiClient } from '@/lib/api-client'
import type {
  ProductsResponse,
  ProductResponse,
  ProductsQuery,
  SyncStatusResponse,
  SyncStartResponse,
  SyncStartRequest,
  ProductCountResponse,
  ProductDeleteResponse,
  ProductImagesResponse,
  ProductImageResponse,
  ImageSyncResponse,
  ImageSyncConfig,
  ImageSyncConfigResponse,
  BatchImageSyncResponse,
  ProductAssetsResponse,
} from '@/types/products'

const API_BASE = '/api/v1'

// =============================================================================
// GET PRODUCTS
// =============================================================================

/**
 * Lädt Produkte des aktuellen Workspaces
 */
export async function getProducts(query?: ProductsQuery): Promise<ProductsResponse> {
  const params = new URLSearchParams()
  
  if (query?.page) params.append('page', query.page.toString())
  if (query?.page_size) params.append('page_size', query.page_size.toString())
  if (query?.integration_id) params.append('integration_id', query.integration_id)
  if (query?.provider) params.append('provider', query.provider)
  if (query?.search) params.append('search', query.search)
  if (query?.is_active !== undefined) params.append('is_active', query.is_active.toString())
  if (query?.include) params.append('include', query.include)
  if (query?.sort_by) params.append('sort_by', query.sort_by)
  if (query?.sort_dir) params.append('sort_dir', query.sort_dir)
  if (query?.quantity_30d_min !== undefined) params.append('quantity_30d_min', query.quantity_30d_min.toString())
  if (query?.revenue_30d_min !== undefined) params.append('revenue_30d_min', query.revenue_30d_min.toString())
  if (query?.quantity_90d_min !== undefined) params.append('quantity_90d_min', query.quantity_90d_min.toString())
  if (query?.revenue_90d_min !== undefined) params.append('revenue_90d_min', query.revenue_90d_min.toString())
  if (query?.has_image !== undefined) params.append('has_image', query.has_image.toString())
  if (query?.synced_after !== undefined && query.synced_after !== '') params.append('synced_after', query.synced_after)

  const url = params.toString() 
    ? `${API_BASE}/products?${params.toString()}`
    : `${API_BASE}/products`
  
  const response = await apiClient.get<ProductsResponse>(url)
  return response.data
}

/**
 * Lädt ein einzelnes Produkt
 */
export async function getProduct(productId: string): Promise<ProductResponse> {
  const response = await apiClient.get<ProductResponse>(
    `${API_BASE}/products/${productId}`
  )
  return response.data
}

/**
 * Löscht ein Produkt
 */
export async function deleteProduct(productId: string): Promise<ProductDeleteResponse> {
  const response = await apiClient.delete<ProductDeleteResponse>(
    `${API_BASE}/products/${productId}`
  )
  return response.data
}

/**
 * Aktualisiert Produkt-Metadaten (brand_id, family_id)
 */
export async function updateProduct(
  productId: string,
  data: { brand_id?: string | null; family_id?: string | null }
): Promise<{ success: boolean }> {
  const response = await apiClient.patch<{ success: boolean }>(
    `${API_BASE}/products/${productId}`,
    data
  )
  return response.data
}

/**
 * Setzt is_active für mehrere Produkte (Bulk)
 */
export async function bulkSetActive(
  productIds: string[],
  isActive: boolean
): Promise<{ success: boolean; updated_count?: number; error?: string }> {
  const response = await apiClient.post<{ success: boolean; updated_count?: number; error?: string }>(
    `${API_BASE}/products/bulk/update`,
    { product_ids: productIds, updates: { is_active: isActive } }
  )
  return response.data
}

/**
 * Zählt Produkte
 */
export async function getProductCount(
  provider?: string,
  integrationId?: string
): Promise<ProductCountResponse> {
  const params = new URLSearchParams()
  if (provider) params.append('provider', provider)
  if (integrationId) params.append('integration_id', integrationId)
  
  const queryString = params.toString()
  const url = queryString 
    ? `${API_BASE}/products/count?${queryString}`
    : `${API_BASE}/products/count`
  
  const response = await apiClient.get<ProductCountResponse>(url)
  return response.data
}

// =============================================================================
// SYNC
// =============================================================================

/**
 * Startet eine Produkt-Synchronisation
 */
export async function startProductSync(
  data: SyncStartRequest
): Promise<SyncStartResponse> {
  const response = await apiClient.post<SyncStartResponse>(
    `${API_BASE}/products/sync`,
    data
  )
  return response.data
}

/**
 * Lädt den aktuellen Sync-Status
 */
export async function getSyncStatus(
  provider: string = 'jtl',
  integrationId?: string
): Promise<SyncStatusResponse> {
  const params = new URLSearchParams()
  params.append('provider', provider)
  if (integrationId) params.append('integration_id', integrationId)

  const response = await apiClient.get<SyncStatusResponse>(
    `${API_BASE}/products/sync/status?${params.toString()}`
  )
  return response.data
}

/**
 * Setzt einen hängengebliebenen Sync-Status zurück
 */
export async function resetSyncStatus(
  provider: string = 'jtl'
): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await apiClient.post<{ success: boolean; message?: string; error?: string }>(
    `${API_BASE}/products/sync/reset?provider=${provider}`
  )
  return response.data
}

// =============================================================================
// PRODUCT IMAGES
// =============================================================================

/**
 * Lädt alle Bilder eines Produkts
 */
export async function getProductImages(productId: string): Promise<ProductImagesResponse> {
  const response = await apiClient.get<ProductImagesResponse>(
    `${API_BASE}/products/${productId}/images`
  )
  return response.data
}

/**
 * Lädt das erste Bild eines Produkts (mit on-demand Sync)
 */
export async function getFirstProductImage(productId: string): Promise<ProductImageResponse> {
  const response = await apiClient.get<ProductImageResponse>(
    `${API_BASE}/products/${productId}/images/first`
  )
  return response.data
}

/**
 * Synchronisiert Bilder für ein einzelnes Produkt
 */
export async function syncProductImages(
  productId: string,
  onlyFirst: boolean = false
): Promise<ImageSyncResponse> {
  const response = await apiClient.post<ImageSyncResponse>(
    `${API_BASE}/products/${productId}/images/sync`,
    { only_first: onlyFirst }
  )
  return response.data
}

/**
 * Speichert die neue Reihenfolge der Produktbilder
 */
export interface ReorderImagesRequest {
  /** Array der Link-IDs in neuer Reihenfolge */
  link_ids: string[]
  /** Optional: Channel-ID (Default: JTL-Wawi "1-1-1") */
  channel_id?: string
}

export interface ReorderImagesResponse {
  success: boolean
  error?: string
  updated_count?: number
  jtl_synced?: boolean
  jtl_updated_count?: number
  jtl_errors?: string[]
}

/**
 * Aktualisiert die Reihenfolge der Produktbilder für einen Channel.
 * Synchronisiert automatisch mit JTL.
 */
export async function reorderProductImages(
  productId: string,
  linkIds: string[],
  channelId?: string
): Promise<ReorderImagesResponse> {
  const response = await apiClient.put<ReorderImagesResponse>(
    `${API_BASE}/products/${productId}/images/reorder`,
    {
      link_ids: linkIds,
      ...(channelId && { channel_id: channelId }),
    }
  )
  return response.data
}

// =============================================================================
// IMAGE SYNC CONFIG
// =============================================================================

/**
 * Lädt die Bild-Sync-Konfiguration
 */
export async function getImageSyncConfig(): Promise<ImageSyncConfigResponse> {
  const response = await apiClient.get<ImageSyncConfigResponse>(
    `${API_BASE}/workspace-settings/image-sync`
  )
  return response.data
}

/**
 * Aktualisiert die Bild-Sync-Konfiguration
 */
export async function updateImageSyncConfig(
  config: Partial<ImageSyncConfig>
): Promise<ImageSyncConfigResponse> {
  const response = await apiClient.put<ImageSyncConfigResponse>(
    `${API_BASE}/workspace-settings/image-sync`,
    config
  )
  return response.data
}

/**
 * Startet einen Batch-Sync für alle Produktbilder
 */
export async function startBatchImageSync(): Promise<BatchImageSyncResponse> {
  const response = await apiClient.post<BatchImageSyncResponse>(
    `${API_BASE}/images/batch-sync`
  )
  return response.data
}

// =============================================================================
// CHANNEL IMAGES (Content-Addressing)
// =============================================================================

export interface ImageChannel {
  channel_id: string
  jtl_channel_id: string
  channel_name: string
  image_count: number
  filled_slots: number[]
}

export interface ImageChannelsResponse {
  success: boolean
  channels: ImageChannel[]
}

export interface ChannelSlot {
  slot: number
  empty: boolean
  id?: string
  asset_id?: string
  // SG-10: no s3_key / presigned s3_url in the response — build the image
  // URL from asset_id via the asset proxy (/api/v1/asset/{id}/{variant}).
  width?: number
  height?: number
  alt_text?: string
  custom_filename?: string
  jtl_image_id?: number
  shared_count?: number
}

export interface ChannelImagesResponse {
  success: boolean
  slots: ChannelSlot[]
}

export interface SetSlotRequest {
  asset_id: string
  alt_text?: string
  custom_filename?: string
}

export interface SetSlotResponse {
  success: boolean
  slot: number
  asset_id: string
  error?: string
}

/**
 * Lädt alle Sales Channels mit Bildanzahl für ein Produkt
 */
export async function getProductImageChannels(productId: string): Promise<ImageChannelsResponse> {
  const response = await apiClient.get<ImageChannelsResponse>(
    `${API_BASE}/products/${productId}/image-channels`
  )
  return response.data
}

/**
 * Lädt alle Bilder eines Channels (Slot-basiert)
 */
export async function getChannelImages(
  productId: string,
  channelId: string,
  maxSlots: number = 10
): Promise<ChannelImagesResponse> {
  const response = await apiClient.get<ChannelImagesResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/images?max_slots=${maxSlots}`
  )
  return response.data
}

/**
 * Setzt ein Asset in einen Slot
 */
export async function setChannelSlot(
  productId: string,
  channelId: string,
  slot: number,
  data: SetSlotRequest
): Promise<SetSlotResponse> {
  const response = await apiClient.post<SetSlotResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/slots/${slot}`,
    data
  )
  return response.data
}

/**
 * Leert einen Slot
 */
export async function clearChannelSlot(
  productId: string,
  channelId: string,
  slot: number
): Promise<{ success: boolean }> {
  const response = await apiClient.delete<{ success: boolean }>(
    `${API_BASE}/products/${productId}/channels/${channelId}/slots/${slot}`
  )
  return response.data
}

// =============================================================================
// ASSET POOL
// =============================================================================

// Re-export ProductAsset from types for linked product assets
export type { ProductAsset, ProductAssetsResponse } from '@/types/products'

// AssetPoolItem represents a deduplicated asset in the asset pool
export interface AssetPoolItem {
  id: string
  content_hash: string
  s3_key: string
  width: number
  height: number
  mime_type: string
  bytesize: number
  channel_count: number
  thumbnail_url?: string
}

export interface AssetPoolResponse {
  success: boolean
  assets: AssetPoolItem[]
}

export interface AssetUsage {
  total_channels: number
  total_usages: number
  channel_ids: string[]
}

export interface AssetUsageResponse {
  success: boolean
  usage: AssetUsage
}

export interface AssetVariant {
  id: string
  purpose: string
  fingerprint: string
  status: string
  s3_key: string
  width: number
  height: number
}

export interface AssetVariantsResponse {
  success: boolean
  variants: AssetVariant[]
}

/**
 * Lädt alle deduplizierten Assets eines Produkts (Asset Pool)
 */
export async function getProductAssetPool(productId: string): Promise<AssetPoolResponse> {
  const response = await apiClient.get<AssetPoolResponse>(
    `${API_BASE}/products/${productId}/asset-pool`
  )
  return response.data
}

/**
 * Lädt die Nutzungsstatistik eines Assets
 */
export async function getAssetUsage(assetId: string): Promise<AssetUsageResponse> {
  const response = await apiClient.get<AssetUsageResponse>(
    `${API_BASE}/assets/${assetId}/usage`
  )
  return response.data
}

/**
 * Lädt alle Varianten eines Assets
 */
export async function getAssetVariants(assetId: string): Promise<AssetVariantsResponse> {
  const response = await apiClient.get<AssetVariantsResponse>(
    `${API_BASE}/assets/${assetId}/variants`
  )
  return response.data
}

// =============================================================================
// KI-REFERENZBILDER
// =============================================================================

export interface AIRefSlot {
  slot: number
  is_empty: boolean
  asset_id?: string
  role?: string
  s3_key_original?: string
  s3_key_preview?: string
  ai_ref_url?: string  // Pre-signed URL für Bildgenerierung
  width?: number
  height?: number
  thumbnail_url?: string
}

export interface AIRefSlotsResponse {
  success: boolean
  slots: AIRefSlot[]
}

export interface SetAIRefRequest {
  slot: number
  asset_id: string
  role?: string
}

export interface SetAIRefResponse {
  success: boolean
  slot: number
  asset_id: string
  variant_id?: string
  error?: string
}

export interface AIRefSummaryEntry {
  has_packshot_ref: boolean
  packshot_asset_id: string | null
  packshot_thumbnail_url: string | null
}

/**
 * Batch: KI-Referenz-Status für mehrere Produkte (für List View)
 */
export async function getAIReferenceSummary(
  productIds: string[]
): Promise<Record<string, AIRefSummaryEntry>> {
  if (!productIds.length) return {}
  const response = await apiClient.get<{ success: boolean; summary: Record<string, AIRefSummaryEntry> }>(
    `${API_BASE}/products/ai-reference-summary`,
    { params: { product_ids: productIds.join(',') } }
  )
  return response.data.summary ?? {}
}

/**
 * Lädt alle 4 KI-Referenz-Slots eines Produkts
 */
export async function getAIReferenceImages(productId: string): Promise<AIRefSlotsResponse> {
  const response = await apiClient.get<AIRefSlotsResponse>(
    `${API_BASE}/products/${productId}/ai-reference-images`
  )
  return response.data
}

/**
 * Setzt ein Asset als KI-Referenz in einen Slot
 */
export async function setAIReferenceImage(
  productId: string,
  data: SetAIRefRequest
): Promise<SetAIRefResponse> {
  const response = await apiClient.post<SetAIRefResponse>(
    `${API_BASE}/products/${productId}/ai-reference-images`,
    data
  )
  return response.data
}

/**
 * Entfernt ein KI-Referenzbild aus einem Slot
 */
export async function deleteAIReferenceImage(
  productId: string,
  slot: number
): Promise<{ success: boolean }> {
  const response = await apiClient.delete<{ success: boolean }>(
    `${API_BASE}/products/${productId}/ai-reference-images/${slot}`
  )
  return response.data
}

// =============================================================================
// ACTIVE SALES CHANNELS (Provider-Sync)
// =============================================================================

export interface ActiveChannel {
  id: string
  channel_id: string
  channel_name: string
  /** Aufgelöster Channel-Name (z.B. "JTL-Wawi - Alle Kanäle") */
  channel_display_name: string
  channel_type: number
  is_active: boolean
  image_count: number
  capabilities: {
    item?: {
      Descriptions?: boolean
      OnlineShopActivation?: boolean
      Prices?: boolean
      SpecialPrices?: boolean
      Images?: boolean
    }
    category?: {
      Descriptions?: boolean
      OnlineShopActivation?: boolean
    }
  }
}

export interface ActiveChannelsResponse {
  success: boolean
  channels: ActiveChannel[]
  integration_id?: string
  product_external_id?: string
  message?: string
}

export interface NormalizedImage {
  external_id: string
  channel_id: string
  /** Aufgelöster Channel-Name (z.B. "JTL-Fulfillment Network - Amazon.de") */
  channel_display_name?: string
  sort_order: number
  bytesize: number
  width: number
  height: number
  filename?: string
  mime_type?: string
}

export interface ImageDiffResponse {
  success: boolean
  has_changes: boolean
  added: NormalizedImage[]
  removed: string[]
  modified: NormalizedImage[]
  unchanged_count: number
}

export interface ChannelSyncResponse {
  success: boolean
  synced: boolean
  added: number
  updated: number
  deleted: number
  error?: string
}

/**
 * Lädt alle aktiven Sales Channels eines Produkts
 */
export async function getActiveChannels(productId: string): Promise<ActiveChannelsResponse> {
  const response = await apiClient.get<ActiveChannelsResponse>(
    `${API_BASE}/products/${productId}/active-channels`
  )
  return response.data
}

/**
 * Prüft ob es Unterschiede zwischen Provider und DB gibt
 */
export async function getChannelDiff(
  productId: string,
  channelId: string
): Promise<ImageDiffResponse> {
  const response = await apiClient.get<ImageDiffResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/diff`
  )
  return response.data
}

/**
 * Synchronisiert Bilder eines Sales Channels
 * 
 * @param force - Wenn true, alle Bilder neu synchronisieren (nicht nur Diff)
 */
export async function syncChannelImages(
  productId: string,
  channelId: string,
  force: boolean = false
): Promise<ChannelSyncResponse> {
  const response = await apiClient.post<ChannelSyncResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/sync`,
    { force }
  )
  return response.data
}

/**
 * Lädt alle Assets eines Produkts (global + channel-spezifisch) mit Channel-Zuordnung.
 *
 * @param productId - Product ID
 * @param channelId - Optional: Filter auf spezifischen Channel
 * @returns Assets gruppiert nach Channel
 */
export async function getProductAssets(
  productId: string,
  channelId?: string
): Promise<ProductAssetsResponse> {
  const params = new URLSearchParams()
  if (channelId) params.append('channel_id', channelId)

  const queryString = params.toString()
  const url = queryString
    ? `${API_BASE}/products/${productId}/assets?${queryString}`
    : `${API_BASE}/products/${productId}/assets`

  const response = await apiClient.get<ProductAssetsResponse>(url)
  return response.data
}

// =============================================================================
// CHANNEL IMAGE UPLOAD
// =============================================================================

export interface UploadChannelImageResponse {
  success: boolean
  asset_id: string
  link_id: string
  slot: number
  jtl_synced: boolean
  jtl_image_id?: number
  error?: string
}

/**
 * Response für pending upload (nur Asset, kein Link).
 */
export interface UploadPendingImageResponse {
  success: boolean
  asset_id: string
  content_hash: string
  width: number
  height: number
  error?: string
}

/**
 * Lädt ein Bild als "pending" Asset hoch (ohne Link).
 * Der Link wird erst beim "Speichern" erstellt.
 */
export async function uploadPendingImage(
  productId: string,
  file: File
): Promise<UploadPendingImageResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post<UploadPendingImageResponse>(
    `${API_BASE}/products/${productId}/upload-pending`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  )
  return response.data
}

/**
 * Lädt ein neues Bild zu einem Channel hoch.
 */
export async function uploadImageToChannel(
  productId: string,
  channelId: string,
  file: File
): Promise<UploadChannelImageResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post<UploadChannelImageResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/images`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  )
  return response.data
}

/**
 * Verknüpft ein existierendes Asset mit einem Channel.
 */
export async function linkAssetToChannel(
  productId: string,
  channelId: string,
  assetId: string,
  targetSlot?: number
): Promise<UploadChannelImageResponse> {
  const response = await apiClient.post<UploadChannelImageResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/images`,
    { asset_id: assetId, ...(targetSlot != null && { target_slot: targetSlot }) }
  )
  return response.data
}

/**
 * Transferiert ein generiertes Bild (aus generated_images) zu einem Produkt.
 * Erstellt dabei automatisch ein Asset und verknüpft es mit dem Channel.
 */
export interface TransferImageResponse {
  success: boolean
  asset_id?: string
  link_id?: string
  already_transferred?: boolean
  error?: string
}

export async function transferGeneratedImageToProduct(
  generatedImageId: string,
  productId: string,
  channelId: string
): Promise<TransferImageResponse> {
  const response = await apiClient.post<TransferImageResponse>(
    `${API_BASE}/images/${generatedImageId}/transfer-to-product/${productId}`,
    {
      target_type: 'collection',
      channel_id: channelId
    }
  )
  return response.data
}

/**
 * Löscht ein Bild aus einem Channel-Slot.
 * Verwendet für pending uploads beim Verwerfen.
 */
export interface DeleteChannelImageResponse {
  success: boolean
  error?: string
}

export async function deleteChannelImage(
  productId: string,
  channelId: string,
  slot: number
): Promise<DeleteChannelImageResponse> {
  const response = await apiClient.delete<DeleteChannelImageResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/images/${slot}`
  )
  return response.data
}

// =============================================================================
// UNUSED ASSETS (for Gallery)
// =============================================================================

export interface UnusedAsset {
  id: string
  content_hash: string
  s3_url: string
  width: number
  height: number
  mime_type: string
  bytesize: number
  created_at: string
}

export interface UnusedAssetsResponse {
  success: boolean
  assets: UnusedAsset[]
  total: number
  error?: string
}

/**
 * Lädt alle ungenutzten Assets (nicht in product_asset_links).
 */
export async function getUnusedAssets(
  limit: number = 100,
  offset: number = 0
): Promise<UnusedAssetsResponse> {
  const params = new URLSearchParams()
  params.append('limit', limit.toString())
  params.append('offset', offset.toString())

  const response = await apiClient.get<UnusedAssetsResponse>(
    `${API_BASE}/assets/unused?${params.toString()}`
  )
  return response.data
}

// =============================================================================
// SALES METRICS (Phase 3)
// =============================================================================

export interface SalesMetrics {
  quantity_30d: number
  revenue_30d: string
  quantity_90d: number
  revenue_90d: string
  last_sale_date: string | null
}

export interface SalesMetricsResponse {
  success: boolean
  metrics: SalesMetrics
  error?: string
}

/**
 * Get sales metrics for a product.
 */
export async function getProductSalesMetrics(productId: string): Promise<SalesMetricsResponse> {
  const response = await apiClient.get<SalesMetricsResponse>(
    `${API_BASE}/products/${productId}/sales-metrics`
  )
  return response.data
}

// =============================================================================
// PROVIDER RAW DATA (DEBUGGING)
// =============================================================================

export interface ProviderRawDataResponse {
  success: boolean
  provider: string
  external_id: string
  data: unknown
  count?: number
  info?: string
  error?: string
}

/**
 * Fetch live item data from provider (JTL, Shopify, etc.).
 * Returns raw data WITHOUT syncing to database.
 */
export async function getProviderItemData(productId: string): Promise<ProviderRawDataResponse> {
  const response = await apiClient.get<ProviderRawDataResponse>(
    `${API_BASE}/products/${productId}/provider-raw-data/item`
  )
  return response.data
}

/**
 * Fetch live descriptions data from provider.
 * Returns all descriptions (channel + language combinations) WITHOUT syncing.
 */
export async function getProviderDescriptionsData(productId: string): Promise<ProviderRawDataResponse> {
  const response = await apiClient.get<ProviderRawDataResponse>(
    `${API_BASE}/products/${productId}/provider-raw-data/descriptions`
  )
  return response.data
}

/**
 * Fetch live images metadata from provider.
 * Returns image list WITHOUT syncing.
 */
export async function getProviderImagesData(productId: string): Promise<ProviderRawDataResponse> {
  const response = await apiClient.get<ProviderRawDataResponse>(
    `${API_BASE}/products/${productId}/provider-raw-data/images`
  )
  return response.data
}

// =============================================================================
// CHANNEL BULLETS (Remake Apply read-side)
// =============================================================================

export interface ChannelBullets {
  bullet_1: string | null
  bullet_2: string | null
  bullet_3: string | null
  bullet_4: string | null
  bullet_5: string | null
}

export interface ChannelBulletsResponse {
  bullets: ChannelBullets
  /** True when all 5 bullet slots have a customfield_id configured. */
  mapping_complete: boolean
  /**
   * Backend-keywords value resolved from JTL custom-field when the channel
   * has a `keywords` mapping; null when no mapping is configured (caller falls
   * back to JTLDescription.SeoMetaKeywords).
   */
  keywords?: string | null
}

/**
 * Lädt die aktuellen Bullet-Werte (1..5) eines Produkts für einen Channel.
 * Liest aus JTL Custom-Fields via workspace_integrations.config.bullet_mapping.
 */
export async function getChannelBullets(
  productId: string,
  channelId: string
): Promise<ChannelBulletsResponse> {
  const response = await apiClient.get<ChannelBulletsResponse>(
    `${API_BASE}/products/${productId}/channels/${channelId}/bullets`
  )
  return response.data
}

// =============================================================================
// CROSS-CHANNEL IMAGE COPY (ADR-009 Phase 4)
// =============================================================================

export interface CopyImageToChannelRequest {
  source_asset_id: string
  source_channel_id?: string | null // null = Global
  target_slot?: number
}

export interface CopyImageToChannelResponse {
  success: boolean
  link?: {
    id: string
    asset_id: string
    slot: number
    channel_id: string
  }
  error?: string
}

/**
 * Kopiert ein Bild von Global oder einem anderen Channel in den Ziel-Channel.
 * Ersetzt ein existierendes Bild im selben Slot oder fügt am Ende hinzu.
 */
export async function copyImageToChannel(
  productId: string,
  targetChannelId: string,
  data: CopyImageToChannelRequest
): Promise<CopyImageToChannelResponse> {
  const response = await apiClient.post<CopyImageToChannelResponse>(
    `${API_BASE}/products/${productId}/channels/${targetChannelId}/copy-image`,
    data
  )
  return response.data
}

